import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive language from URL path
  const lang = useMemo(() => {
    return location.pathname.startsWith('/es') ? 'es' : 'en';
  }, [location.pathname]);

  // Get the current page path without lang prefix
  const getBasePath = useCallback(() => {
    const path = location.pathname;
    if (path.startsWith('/es')) {
      const rest = path.slice(3); // remove "/es"
      return rest || '/';
    }
    return path;
  }, [location.pathname]);

  // Build a localized path
  const localePath = useCallback((basePath, targetLang) => {
    const target = targetLang || lang;
    const clean = basePath === '/' ? '' : basePath;
    if (target === 'es') {
      return `/es${clean}`;
    }
    return basePath;
  }, [lang]);

  // Toggle between EN and ES — navigates to the other version
  const toggleLang = useCallback(() => {
    const basePath = getBasePath();
    if (lang === 'en') {
      navigate(`/es${basePath === '/' ? '' : basePath}`);
    } else {
      navigate(basePath);
    }
  }, [lang, getBasePath, navigate]);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations['en']?.[key] || key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, localePath, getBasePath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
